import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Paper,
  Button,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Home as HomeIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CompletedIcon,
  Check as CheckIcon,
  Schedule as PendingIcon,
  PlayArrow as InProgressIcon,
  Cancel as CancelledIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchDoctorDashboard } from '../../store/slices/dashboardSlice';

/**
 * Today's Schedule Page
 * Full day's appointment timeline for Government Doctors.
 *
 * @author Civilhealth Development Team
 */

interface Appointment {
  id: number;
  bookingNumber: string;
  appointmentTime?: string;
  startTime: string;
  endTime: string;
  patientName: string;
  officerName?: string;
  officerDesignation?: string;
  officerDepartment?: string;
  officerCadre?: string;
  appointmentType: string;
  status: string;
  symptoms?: string;
  visitAddress?: string;
  isEmergency?: boolean;
  doctorName?: string;
  doctorSpecialization?: string;
}

// Status Configuration - HIGH CONTRAST: Light background + Dark icon
const getStatusConfig = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'PENDING':
      return {
        label: 'Pending',
        color: '#B45309',       // Amber 700 - DARK for visibility
        bgColor: '#FEF3C7',      // Amber 100 - LIGHT background
        chipColor: 'warning' as const,
        icon: PendingIcon
      };
    case 'CONFIRMED':
      return {
        label: 'Confirmed',
        color: '#B45309',       // Amber 700 - DARK
        bgColor: '#FEF3C7',      // Amber 100
        chipColor: 'warning' as const,
        icon: PendingIcon
      };
    case 'DOCTOR_ASSIGNED':
    case 'ACCEPTED':
      return {
        label: 'Accepted',
        color: '#15803D',       // Green 700 - DARK for visibility
        bgColor: '#DCFCE7',      // Green 100 - LIGHT background
        chipColor: 'success' as const,
        icon: CheckIcon
      };
    case 'DOCTOR_ON_THE_WAY':
    case 'IN_PROGRESS':
      return {
        label: 'In Progress',
        color: '#1D4ED8',       // Blue 700 - DARK for visibility
        bgColor: '#DBEAFE',      // Blue 100 - LIGHT background
        chipColor: 'info' as const,
        icon: InProgressIcon
      };
    case 'COMPLETED':
      return {
        label: 'Completed',
        color: '#166534',       // Green 800 - DARK for visibility
        bgColor: '#DCFCE7',      // Green 100
        chipColor: 'success' as const,
        icon: CompletedIcon
      };
    case 'CANCELLED':
      return {
        label: 'Cancelled',
        color: '#DC2626',       // Red 600 - DARK for visibility
        bgColor: '#FEE2E2',      // Red 100 - LIGHT background
        chipColor: 'error' as const,
        icon: CancelledIcon
      };
    default:
      return {
        label: status || 'Unknown',
        color: '#374151',       // Gray 700 - DARK
        bgColor: '#F3F4F6',      // Gray 100 - LIGHT
        chipColor: 'default' as const,
        icon: PendingIcon
      };
  }
};

const TodaySchedulePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { doctorDashboard, loading } = useSelector((state: RootState) => state.dashboard);

  const [appointments, setAppointments] = useState<Appointment[]>([]);

  useEffect(() => {
    if (user?.role === 'GOVERNMENT_DOCTOR') {
      dispatch(fetchDoctorDashboard() as any);
    }
  }, [dispatch, user?.role]);

  useEffect(() => {
    if (doctorDashboard?.todayAppointments) {
      // Filter out CLOSED appointments and sort by appointment time
      const filtered = doctorDashboard.todayAppointments.filter((apt: Appointment) => {
        const status = apt.status?.toUpperCase();
        return status !== 'CLOSED' && status !== 'COMPLETED';
      });
      const sorted = [...filtered].sort((a: Appointment, b: Appointment) => {
        const timeA = a.appointmentTime || a.startTime || '00:00';
        const timeB = b.appointmentTime || b.startTime || '00:00';
        return timeA.localeCompare(timeB);
      });
      setAppointments(sorted);
    }
  }, [doctorDashboard]);

  const handleRefresh = () => {
    dispatch(fetchDoctorDashboard() as any);
  };

  const handleAppointmentClick = (appointmentId: number) => {
    navigate(`/doctor/visit/${appointmentId}`);
  };

  const formatTime = (time: string) => {
    if (!time) return '--:--';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  };

  // Group appointments by time period
  const groupedAppointments = appointments.reduce((groups: Record<string, Appointment[]>, apt: Appointment) => {
    const time = apt.appointmentTime || apt.startTime || '00:00';
    const hour = parseInt(time.split(':')[0], 10);
    let period = 'Morning';
    if (hour >= 12 && hour < 17) period = 'Afternoon';
    else if (hour >= 17) period = 'Evening';

    if (!groups[period]) groups[period] = [];
    groups[period].push(apt);
    return groups;
  }, {});

  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <Box sx={{ p: 3, flex: 1, overflow: 'auto', bgcolor: 'transparent' , minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Today's Schedule
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mt: 0.5 }}>
            {formattedDate}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ borderRadius: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate('/doctor/workflow')}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' }}
          >
            Go to Workflow
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                  <CalendarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                    {appointments.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Active Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#B45309', border: '2px solid rgba(180, 83, 9, 0.2)' }}>
                  <PendingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                    {appointments.filter(a => ['PENDING', 'CONFIRMED'].includes(a.status?.toUpperCase())).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                    {appointments.filter(a => ['ACCEPTED', 'IN_PROGRESS', 'VISITED'].includes(a.status?.toUpperCase())).length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    In Progress
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card
            sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', cursor: 'pointer' }}
            onClick={() => navigate('/doctor/appointments')}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* HIGH CONTRAST: Light background + Dark icon */}
                <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                  <CompletedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A2E' }}>
                    {doctorDashboard?.todayAppointments?.filter((a: Appointment) => ['CLOSED', 'COMPLETED'].includes(a.status?.toUpperCase())).length || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    Closed Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Timeline */}
      {loading ? (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 2, color: '#64748b', textAlign: 'center' }}>
              Loading schedule...
            </Typography>
          </CardContent>
        </Card>
      ) : appointments.length === 0 ? (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <CalendarIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
              No Appointments Today
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', mb: 3 }}>
              You don't have any appointments scheduled for today.
            </Typography>
            <Button
              variant="contained"
              startIcon={<CalendarIcon />}
              onClick={() => navigate('/appointments')}
              sx={{ borderRadius: 2 }}
            >
              View All Appointments
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {Object.entries(groupedAppointments).map(([period, periodAppointments]) => (
            <Box key={period} sx={{ mb: 4 }}>
              {/* Period Header */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: '#64748b',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TimeIcon sx={{ fontSize: 18 }} />
                {period} ({(periodAppointments as Appointment[]).length})
              </Typography>

              {/* Timeline Items */}
              {(periodAppointments as Appointment[]).map((appointment, index) => {
                const statusConfig = getStatusConfig(appointment.status);
                const StatusIcon = statusConfig.icon;

                return (
                  <Card
                    key={appointment.id || index}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      borderLeft: `4px solid ${statusConfig.color}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => handleAppointmentClick(appointment.id)}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        {/* Time */}
                        <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 700,
                              color: statusConfig.color,
                            }}
                          >
                            {formatTime(appointment.appointmentTime || appointment.startTime)}
                          </Typography>
                          {appointment.endTime && (
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              to {formatTime(appointment.endTime)}
                            </Typography>
                          )}
                        </Box>

                        {/* Timeline Connector */}
                        <Box
                          sx={{
                            width: 2,
                            bgcolor: 'transparent' ,
                            borderRadius: 1,
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: statusConfig.color,
                              border: '2px solid #fff',
                            },
                          }}
                        />

                        {/* Content */}
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {appointment.patientName || appointment.officerName || 'Patient'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={appointment.officerCadre || 'Officer'}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    bgcolor: 'transparent' ,
                                    color: '#2563eb',
                                  }}
                                />
                                {appointment.isEmergency && (
                                  <Chip
                                    label="Emergency"
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      bgcolor: 'transparent' ,
                                      color: '#dc2626',
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                            <Chip
                              icon={<StatusIcon sx={{ fontSize: 16 }} />}
                              label={statusConfig.label}
                              sx={{
                                fontWeight: 600,
                                bgcolor: statusConfig.bgColor,
                                color: statusConfig.color,
                              }}
                            />
                          </Box>

                          {/* Details Grid */}
                          <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Patient
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                    {appointment.officerName || 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HomeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                <Box>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Visit Type
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                    {appointment.appointmentType || 'Home Visit'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            {appointment.visitAddress && (
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <HospitalIcon sx={{ fontSize: 18, color: '#64748b', mt: 0.25 }} />
                                  <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                                      Address
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                      {appointment.visitAddress}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                            {appointment.symptoms && (
                              <Grid item xs={12}>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                  Symptoms: {appointment.symptoms}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>

                          {/* Action Button */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                              size="small"
                              endIcon={<ArrowIcon />}
                              sx={{
                                color: '#2563eb',
                                fontWeight: 600,
                                '&:hover': { bgcolor: 'transparent' },
                              }}
                            >
                              View Details
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TodaySchedulePage;